import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import React from 'react';

import { TablePillProps, TablePillSize } from './TablePill.props';
import { lightTheme } from '../../../configs/themes';

/**
 * The tones live in the theme (`palette.v4.pill`), not here, so the one rule
 * this repo has about colour — take it from a token — holds for the pill too.
 * `TABLE_PILL_TONES` re-exports them for the tests that measure their contrast.
 */
export const TABLE_PILL_TONES = lightTheme.palette.v4.pill;

/** `sm` is the table size; `md` is for the places a status is the subject
 *  rather than a cell — a detail-panel field, a confirm dialog. */
const SIZES: Record<TablePillSize, { fontSize: string; px: number }> = {
  sm: { fontSize: '10px', px: 0.875 },
  md: { fontSize: '12px', px: 1 },
};

/**
 * The one pill used across the app — list tables, detail panels, dialogs and
 * mobile.
 *
 * There were five implementations before this — two hand-rolled `ActivePill`s,
 * a teal `NodeTypeChip`, a pair of hardcoded palettes in ShipmentTable, and
 * MUI's filled `<Chip>` — so the same idea rendered four different ways, and
 * the loudest of them was illegible.
 */
export const TablePill: React.FC<TablePillProps> = ({
  children,
  tone = 'neutral',
  size = 'sm',
  ariaLabel,
  testId,
}) => {
  const theme = useTheme();
  // Read from the theme, but fall back to the same token rather than crash:
  // a good number of tests and fragments build a partial `v4` palette, and an
  // atom this widely used should not be the thing that breaks on one.
  const { bg, fg } = (theme.palette.v4?.pill ?? TABLE_PILL_TONES)[tone];
  const { fontSize, px } = SIZES[size];

  return (
    <Box
      component="span"
      role="status"
      aria-label={ariaLabel}
      data-testid={testId}
      sx={{
        display: 'inline-block',
        bgcolor: bg,
        color: fg,
        fontSize,
        fontWeight: 600,
        lineHeight: 1.6,
        px,
        py: '2px',
        borderRadius: '4px',
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Box>
  );
};
